import { useDispatch, useSelector } from "react-redux"
import { IonDatetime, IonList, IonItem, IonLabel, IonInput, IonButton, IonAlert, IonSelectOption, IonSelect } from "@ionic/react";
import React, { useEffect, useState } from "react";
import { addTrip, selectMode, selectSum } from "./logSlice";
import { LogEntry } from "./logEntry";
import { TripType } from "./tripType";
import { currentStage } from "../pilgrimage/current-stage";
import { useHistory } from "react-router";
import { selectPilgrimage, selectStages } from "../pilgrimage/pilgrimageSlice";
import { createLogEntryInDatabase } from "../../utils/airtable-api";
import { selectUserName } from "../user/userSlice";

/* Health */
import { Health } from '@ionic-native/health';
import { selectContent } from "../content/contentSlice";
import { Content } from "../content/content";
import { Stage } from "../pilgrimage/stage";
import { milesOrPoints } from "../../app/miles-or-points";
import { ACTIVITIES, Activity } from "./activities";

type AddLogEntryProps = {
  onSubmit?: () => void;
}

export const AddLogEntry = ({ onSubmit } : AddLogEntryProps) => {
  const history = useHistory(),
    dispatch = useDispatch(),
    [date, setDate] = useState<string>(new Date().toISOString()),
    [distance, setDistance] = useState<number>(0),
    [activity, setActivity] = useState<Activity | null>(null),
    [healthDataDistance, setHealthDataDistance] = useState<number | null>(null),
    [healthDataDistanceAvailable, setHealthDataDistanceAvailable] = useState<boolean>(false),
    [notes, setNotes] = useState<string | null>(null),
    [willUnlockNextVideo, setWillUnlockNextVideo] = useState(false),
    [showCongrats, setShowCongrats] = useState(false),
    sum = useSelector(selectSum),
    stages = useSelector(selectStages),
    content = useSelector(selectContent),
    [remainder, setRemainder] = useState<number | undefined>(undefined),
    [stageReached, setStageReached] = useState<Stage | undefined>(undefined),
    [nextVideo, setNextVideo] = useState<Content | null>(null),
    user = useSelector(selectUserName),
    pilgrimage = useSelector(selectPilgrimage),
    mode = useSelector(selectMode),
    today = new Date(),
    y = today.getFullYear().toString(),
    m = (today.getMonth()+1).toString(),
    d = today.getDate().toString();
  
  const ClickToPray = ({ liturgy } : {liturgy: string}) => <CenteredButton
    url={`https://www.venite.app/pray/en/Rite-II/${y}/${m}/${d}/${liturgy}`}
    label="Click to Pray"/>

  // set initial stage
  useEffect(() => {
    const stage = currentStage(sum, stages || []);
    setStageReached(stage?.stageReached);
    setRemainder(stage?.remainder);
  }, [sum, stages]);

  useEffect(() => {
    const lookForHealthData = async () => {
      try {
        const healthAvailable = await Health.isAvailable();
        if(healthAvailable) {
          // check for authorization
          await Health.requestAuthorization([{
            read: ['steps', 'distance']
          }]);
  
          // search for data
          const startDate = new Date(Date.parse(date));
          startDate.setHours(0);
          startDate.setMinutes(0);
          startDate.setSeconds(0);
          startDate.setMilliseconds(0);

          const distanceResults = await Health.queryAggregated({
            startDate, // beginning of the day,
            endDate: new Date(Date.parse(date)),
            dataType: 'distance',
            bucket: 'day'
          });
    
          console.log(`\n\n\n[DISTANCE] HEALTH KIT QUERY RESULTS = ${JSON.stringify(distanceResults)}\n\n\n`);
          
          // try distance first
          if(distanceResults?.length > 0) {
            const distance = Number((distanceResults.map(data => data.value).reduce((a, b) => Number(a) + Number(b), 0) / 1609.34).toFixed(2));
            console.log(`\n\n\nHEALTH KIT DISTANCE IN MILES = ${distance}`);
            setHealthDataDistance(distance);
            setHealthDataDistanceAvailable(true);

            if(distance === 0) {
              // try for steps instead
              const stepResults = await Health.queryAggregated({
                startDate, // beginning of the day,
                endDate: new Date(Date.parse(date)),
                dataType: 'steps',
                bucket: 'day'
              });
    
              console.log(`\n\n\n[STEPS] HEALTH KIT QUERY RESULTS = ${JSON.stringify(stepResults)}\n\n\n`);

              if(stepResults?.length > 0) {
                const distance = Number((stepResults.map(data => data.value).reduce((a, b) => Number(a) + Number(b), 0) / 2000).toFixed(2));
                console.log(`\n\n\nHEALTH KIT DISTANCE IN MILES = ${distance}`);
                setHealthDataDistance(distance);
                setHealthDataDistanceAvailable(true);
              }
            }
          }
        }
      } catch(e) {
        console.warn(`Error trying to use Cordova Health plugin: \n\n, ${e}`)
      }
    };

    if(mode === 'miles' || Number(activity?.id) === TripType.Walk) {
      lookForHealthData();
    }
  }, [date, mode, activity]);  

  // callbacks
  const submitForm = async (distanceBeforeChange : number) => {
    const trip = new LogEntry({
      date,
      type: activity?.id || TripType.Walk,
      distance,
      leg: null,
      notes
    });

    // Update remainder
    const stage = currentStage(sum + distance, stages || []);
    setStageReached(stage?.stageReached);
    setRemainder(stage?.remainder);

    // Unlock next video, if you've traveled far enough
    const notAlreadyUnlocked = (content?.content?.data || []).filter(content => content.unlocked_after > distanceBeforeChange),
      unlocked = notAlreadyUnlocked.filter(content => content.unlocked_after <= sum + distance),
      next = unlocked[unlocked.length - 1];
    if(next && distance >= (next?.unlocked_after - sum)) {
      setNextVideo(next)
      setWillUnlockNextVideo(true);
    } else {
      setNextVideo(null);
      setWillUnlockNextVideo(false);
    }

    // clear fields
    setDate(new Date().toISOString());
    setDistance(0);
    setActivity(null);
    setNotes(null);

    // store it in the DB
    if(user && pilgrimage.id) {
      try {
        const tripId = await createLogEntryInDatabase(trip, pilgrimage.id, user, pilgrimage.homeChurch);
        trip.id = tripId;
        dispatch(addTrip(trip));
      } catch(e) {
        // usually because offline, so couldn't submit to database
        trip.id = 'OFFLINE';
        dispatch(addTrip(trip));
      }
    }
  };

  const onSubmitForm = (e : null | React.FormEvent<HTMLFormElement>) => {
    if(e) {
      e.preventDefault();
    }
    submitForm(sum);
    setShowCongrats(true);
  }

  return (
    <React.Fragment>
      <form onSubmit={onSubmitForm}>
        <IonList>
          <IonItem>
            <IonLabel position="stacked">Date</IonLabel>
            <IonDatetime displayFormat="MMMM D" value={date} onIonChange={e => setDate(e.detail.value || "")}></IonDatetime>
          </IonItem>

          {/* Point-based activity selector */}
          {mode === 'points' && <IonItem
            lines={
              activity?.id && [TripType.MP, TripType.EP, TripType.NP, TripType.Compline, TripType.ListenToAPodcast, TripType.ReadDevotional].includes(activity?.id)
              ? "none"
              : "full"
            }
          >
            <IonLabel position="stacked">Activity</IonLabel>
            <IonSelect
              value={activity?.id.toString()}
              onIonChange={e => {
                const a = ACTIVITIES.find(activity => activity.id === Number(e.detail.value));
                setActivity(a ? a : null);
                console.log('setting distance to', a?.points_per_unit);
                setDistance(a?.points_per_unit ?? 0);
              }}>
              {ACTIVITIES.map(activity =>
                <IonSelectOption key={activity.id} value={activity.id.toString()}>
                  <IonLabel>{activity.name}</IonLabel>
                </IonSelectOption>
              )}
            </IonSelect>
          </IonItem>}

          {/* Links for some activity types */}
          {activity?.id === TripType.MP && <ClickToPray liturgy="morning_prayer"/>}
          {activity?.id === TripType.NP && <ClickToPray liturgy="noonday_prayer"/>}
          {activity?.id === TripType.EP && <ClickToPray liturgy="evening_prayer"/>}
          {activity?.id === TripType.Compline && <ClickToPray liturgy="compline"/>}
          {/* TODO LINKS FOR RESOURCES */}
          {activity?.id === TripType.ListenToAPodcast && <SeeResources link="https://www.smifsc.com/news/advent-online"/>}
          {activity?.id === TripType.ReadDevotional && <SeeResources link="https://www.smifsc.com/news/advent-online"/>}

          {/* Mileage */}
          {(mode === 'miles' || Number(activity?.id) === TripType.Walk) && <IonItem>
            <IonLabel position="stacked">Miles Walked</IonLabel>
            <IonInput
              value={distance ?? 0}
              onIonChange={e => setDistance(Number(e.detail.value ?? "0"))}
              onKeyUp={e => e.key === 'Enter' && onSubmitForm(null)}
            ></IonInput>
          </IonItem>}

          {/* Notes and other logic */}
          <IonItem>
            <IonLabel position="stacked">Notes</IonLabel>
            <IonInput
              value={notes}
              onIonChange={e => setNotes(e.detail.value ? e.detail.value : null)}
              onKeyUp={e => e.key === 'Enter' && onSubmitForm(null)}
            ></IonInput>
          </IonItem>
          <div className="centered-button">
            <IonButton type="submit">
              <IonLabel>Log</IonLabel>
            </IonButton>
          </div>
        </IonList>
      </form>
      <IonAlert isOpen={healthDataDistanceAvailable && Boolean(healthDataDistance)}
        header="We found your walk."
        message={`It looks like you walked ${healthDataDistance} miles today. Would you like to use that to start your log?`}
        buttons={[
          {
            text: 'No, thanks.',
            handler: () => {
              console.log('set show congrats to false');
              setShowCongrats(false)
            }
          },
          {
            text: 'Yes, please!',
            handler: () => {
              setDistance(healthDataDistance || 0);
              setHealthDataDistance(null);
              setHealthDataDistanceAvailable(false);
            }
          }
        ]}
      />
      {stageReached && <IonAlert isOpen={showCongrats}
        header="Good work!"
        message={`You’re well on your way. You’ve got ${remainder?.toFixed(2)} ${milesOrPoints(mode, remainder ?? 0)} to go until ${stageReached.end.name}.`}
        buttons={[
          {
            text: 'Okay!',
            handler: () => {
              if(onSubmit) {
                onSubmit();
              }
              setShowCongrats(false);
            }
          }
        ]}
      />}
      {stageReached && <IonAlert isOpen={showCongrats && Boolean(nextVideo) && willUnlockNextVideo}
        header="You’ve unlocked new content."
        message={`You’ve got ${remainder?.toFixed(2)} ${milesOrPoints(mode, remainder ?? 0)} to go until ${stageReached.end.name}. You’ve unlocked new content, entitled “${nextVideo?.title}.” Would you like to see it now?`}
        buttons={[
          {
            text: 'No thanks.',
            handler: () => {
              if(onSubmit) {
                onSubmit();
              }
              setShowCongrats(false);
            }
          },
          {
            text: 'Yes, please!',
            handler: () => {
              if(onSubmit) {
                onSubmit();
              }
              setShowCongrats(false);
              if(nextVideo) {
                history.push(`/content/${nextVideo.id}`);
              }
            }
          }
        ]}
      />}
      {!stageReached && <IonAlert
        isOpen={true}
        header="Loading Data..."
        message="We’re still loading data for this pilgrimage."
      />}
    </React.Fragment>
  )
}

const CenteredButton = ({ url, label } : { url: string; label: string; }) => <IonItem>
  <div className="centered-button">
    <IonButton
      href={url}
      target="_blank"
      size="default"
      fill="outline"
    >
      <IonLabel>{label}</IonLabel>
    </IonButton>
  </div>
</IonItem>

const SeeResources = ({ link } : { link: string }) => <CenteredButton url={link} label="See Resources"/>